/**
 * The one list of INTERNAL surfaces — FRIDGE-418.
 *
 * docs/Page-Types-And-Templates.md has always said these are internal and
 * "excluded by mandate and NOT page types of the site". Two separate defects
 * came from that being written in prose and nowhere in code:
 *
 *   1. `/_qa/*` was reachable by anyone. Measured on a production build with no
 *      cookie on 08/18/2026: `/_qa/es/home` returned 200 with 458 KB of
 *      rendered page, as did `/_qa/es/marca/lg` and `/_qa/es/coleccion/…`.
 *      It carried `X-Robots-Tag: noindex` and nothing else — and noindex is not
 *      access control. `middleware.ts` now gates it on the admin cookie.
 *
 *   2. The newsletter capture popup fired on `/operations/*`. Its exclusion
 *      list held only `/contact`, `/privacy-policy`, `/terms-of-service`, `/r`
 *      and `/embed`, so every internal admin page fell through to the general
 *      variant and an email-capture card rendered over the operations
 *      dashboards. Observed directly on `/operations/analytics` and
 *      `/operations/seo`.
 *
 * Both fixes read THIS list, so a new internal namespace cannot be gated in one
 * place and forgotten in the other. Adding a prefix here closes it everywhere.
 *
 * Not in this list on purpose:
 *   • `/mockups/*` — internal, but already 404s in production via
 *     `app/mockups/layout.tsx` (`NODE_ENV === 'production' → notFound()`), so
 *     it is unreachable rather than gated. It IS listed for the popup rule
 *     below, because it is reachable in development and a capture card there is
 *     the same noise.
 *   • `/api/*` — guarded separately in middleware.ts; these are page prefixes.
 */

/** Page prefixes that must never be reachable without the admin cookie. */
export const AUTH_GATED_PREFIXES = ['/admin-panel', '/operations', '/_qa'] as const

/**
 * Page prefixes that are internal tooling, whether or not they are auth-gated.
 * No marketing surface — popup, capture bar, promotional interstitial — may
 * render on any of these.
 */
export const INTERNAL_SURFACE_PREFIXES = [
  ...AUTH_GATED_PREFIXES,
  '/mockups',
  '/docs',
] as const

/** Does this path sit under an internal surface? */
export function isInternalSurface(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return INTERNAL_SURFACE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))
}
